// The calendar's read model for the month, week, list and year (DESIGN.md "The
// Calendar in Parts"). Pure and React-free on purpose, so the grid, the header,
// the list and scripts/test-calendar-month.mjs all read the same answer.
//
// Whose turn it is comes from postState, one vocabulary everywhere: the cards,
// the filters, the status line and the peek all speak its `turn` labels.

import type {
  CalendarPlanSlot,
  CalendarPlatform,
} from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { slotHasBrief } from "@/types/contentCalendar.type";
import { derivePostState, clientLabel, type PostState, type TurnOwner } from "../postState";
import { intendedPublicationChannels, publicationIntegrityOf } from "../publicationIntegrity";
import { statusAfterMove } from "../slotStage";

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;
export const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

const DAY_MS = 86_400_000;

// ------------------------------------------------------------------- days

export interface DayCell {
  year: number;
  /** 1-12. */
  month: number;
  day: number;
  /** 0 Sun to 6 Sat. */
  weekday: number;
  /** UTC midnight of the day. */
  utc: number;
  /** "2026-12-17", stable for keys and lookups. */
  key: string;
  /** True when the day belongs to the month the grid is about. */
  inMonth: boolean;
}

export function dayKey(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function cellAt(utc: number, year: number, month: number): DayCell {
  const d = new Date(utc);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return {
    year: y,
    month: m,
    day,
    weekday: d.getUTCDay(),
    utc,
    key: dayKey(y, m, day),
    inMonth: y === year && m === month,
  };
}

/** UTC midnight of the Sunday on or before `utc`. */
export function weekStartOf(utc: number): number {
  const d = new Date(utc);
  const midnight = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return midnight - new Date(midnight).getUTCDay() * DAY_MS;
}

/**
 * Every month grid is six weeks of seven days (DESIGN.md), Sunday first, with
 * the neighbouring months' days filling the edges. Always 42 cells, so the grid
 * never changes height from one month to the next.
 */
export function sixWeekGrid(year: number, month: number): DayCell[] {
  const start = weekStartOf(Date.UTC(year, month - 1, 1));
  return Array.from({ length: 42 }, (_, i) => cellAt(start + i * DAY_MS, year, month));
}

/** The seven days of the week starting `startUtc`, marked against the open month. */
export function weekCells(startUtc: number, year: number, month: number, days = 7): DayCell[] {
  return Array.from({ length: days }, (_, i) => cellAt(startUtc + i * DAY_MS, year, month));
}

/** True when the week (or a tablet's three days) holds at least one day of the open month. */
export function weekTouchesMonth(startUtc: number, year: number, month: number, days = 7): boolean {
  return weekCells(startUtc, year, month, days).some((cell) => cell.inMonth);
}

/** The week a month opens on in Week view: today's week when today is in it, else its first. */
export function defaultWeekStart(year: number, month: number, now: number): number {
  const today = new Date(now);
  if (today.getUTCFullYear() === year && today.getUTCMonth() + 1 === month) return weekStartOf(now);
  return weekStartOf(Date.UTC(year, month - 1, 1));
}

/** "8 – 14 Nov 2026", "29 Nov – 5 Dec 2026", "28 Dec 2026 – 3 Jan 2027" (Pen: Week · 8 to 14 Nov). */
export function weekTitle(startUtc: number, days = 7): string {
  const a = new Date(startUtc);
  const b = new Date(startUtc + (days - 1) * DAY_MS);
  const mon = (d: Date) => MONTH_NAMES[d.getUTCMonth()].slice(0, 3);
  if (a.getUTCFullYear() !== b.getUTCFullYear()) {
    return `${a.getUTCDate()} ${mon(a)} ${a.getUTCFullYear()} – ${b.getUTCDate()} ${mon(b)} ${b.getUTCFullYear()}`;
  }
  if (a.getUTCMonth() !== b.getUTCMonth()) {
    return `${a.getUTCDate()} ${mon(a)} – ${b.getUTCDate()} ${mon(b)} ${b.getUTCFullYear()}`;
  }
  return `${a.getUTCDate()} – ${b.getUTCDate()} ${mon(b)} ${b.getUTCFullYear()}`;
}

/** "Thu 17". */
export function dayShort(cell: Pick<DayCell, "weekday" | "day">): string {
  return `${WEEKDAY_SHORT[cell.weekday]} ${cell.day}`;
}

/** "Thu 17 Dec". */
export function dayMonthShort(cell: Pick<DayCell, "weekday" | "day" | "month">): string {
  return `${WEEKDAY_SHORT[cell.weekday]} ${cell.day} ${MONTH_NAMES[cell.month - 1].slice(0, 3)}`;
}

export function todayUtc(now: number): number {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/**
 * How many cards a day shows before it says "+{n} more". `fitAll` is how many
 * cards the cell holds with nothing else in it, `fitWithMore` how many still fit
 * once the more line takes its room. A day never shows fewer than one.
 */
export function cellOverflow(
  count: number,
  fitAll: number,
  fitWithMore: number,
): { visible: number; more: number } {
  if (count <= 0) return { visible: 0, more: 0 };
  if (count <= fitAll) return { visible: count, more: 0 };
  const visible = Math.min(count, Math.max(1, fitWithMore));
  return { visible, more: count - visible };
}

// ------------------------------------------------------------------ posts

/** The four whose-turn groups the status line and filters count by. */
export type TurnGroup = "you" | "client" | "agent" | "none";

export function turnGroup(owner: TurnOwner): TurnGroup {
  // A late answer is still the client's to give: it counts with the client, in alert ink.
  if (owner === "late") return "client";
  return owner;
}

/** Channel marks in the calendar's fixed order (DESIGN.md), then the rest. */
export const CHANNEL_ORDER: CalendarPlatform[] = [
  "INSTAGRAM",
  "TIKTOK",
  "YOUTUBE",
  "FACEBOOK",
  "PINTEREST",
  "SNAPCHAT",
  "LINKEDIN",
  "REDDIT",
  "SUBSTACK",
];

export function orderChannels(channels: readonly CalendarPlatform[]): CalendarPlatform[] {
  return [...new Set(channels)].sort(
    (a, b) => CHANNEL_ORDER.indexOf(a) - CHANNEL_ORDER.indexOf(b),
  );
}

export const CHANNEL_NAME: Record<CalendarPlatform, string> = {
  INSTAGRAM: "Instagram",
  TIKTOK: "TikTok",
  YOUTUBE: "YouTube",
  FACEBOOK: "Facebook",
  PINTEREST: "Pinterest",
  SNAPCHAT: "Snapchat",
  LINKEDIN: "LinkedIn",
  REDDIT: "Reddit",
  SUBSTACK: "Substack",
};

export interface PillarRef {
  id: string;
  name: string;
  order?: number;
}

/**
 * Each pillar's place in strategy order, for `--co-pillar-{index + 1}`. Pillars
 * a post still carries from an older strategy follow the current ones, so a
 * post never loses its edge after a strategy edit.
 */
export function pillarIndexMap(
  pillars: readonly PillarRef[],
  slotPillarIds: readonly (string | null)[] = [],
): Map<string, number> {
  const map = new Map<string, number>();
  const ordered = [...pillars].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const pillar of ordered) if (!map.has(pillar.id)) map.set(pillar.id, map.size);
  for (const id of slotPillarIds) if (id && !map.has(id)) map.set(id, map.size);
  return map;
}

/** The pillar hue token for a strategy position. Five hues; a sixth wraps. */
export function pillarHue(index: number | null | undefined): string | null {
  if (index === null || index === undefined || index < 0) return null;
  return `var(--co-pillar-${(index % 5) + 1})`;
}

const STAGE_WORD = { concept: "Concept", production: "Approved", live: "Live" } as const;

export function stageWord(state: PostState, isGuest: boolean): string {
  if (isGuest && state.stage !== "live") return "Idea and brief";
  // A quick post never claims an approval: before Live it is "Files".
  if (state.quick && state.stage !== "live") return "Files";
  return STAGE_WORD[state.stage];
}

export interface CalendarPost {
  slot: CalendarPlanSlot;
  state: PostState;
  group: TurnGroup;
  /** Strategy position, or null when the post has no pillar. */
  pillarIndex: number | null;
  channels: CalendarPlatform[];
  /** Every intended channel is live. */
  posted: boolean;
}

export interface ReadOptions {
  briefBySlot?: Map<string, BriefRailInfo>;
  clientName?: string | null;
  isGuest: boolean;
  now: number;
  pillarIndex: Map<string, number>;
  /** The brand's zone, for the times on the cards. */
  timeZone?: string;
}

/** A post has its creative brief from the pick on (the idea and the brief go out together). */
function briefFor(slot: CalendarPlanSlot, briefs: Map<string, BriefRailInfo> | undefined) {
  if (!slotHasBrief(slot)) return undefined;
  return briefs ? (briefs.get(slot.id) ?? null) : undefined;
}

export function readPost(slot: CalendarPlanSlot, options: ReadOptions): CalendarPost {
  const state = derivePostState(slot, {
    brief: briefFor(slot, options.briefBySlot),
    clientName: options.clientName,
    isGuest: options.isGuest,
    now: options.now,
    timeZone: options.timeZone,
  });
  return {
    slot,
    state,
    group: turnGroup(state.turn.owner),
    pillarIndex: slot.pillarId ? (options.pillarIndex.get(slot.pillarId) ?? null) : null,
    channels: orderChannels(intendedPublicationChannels(slot)),
    posted: publicationIntegrityOf(slot).state === "live",
  };
}

export function readPosts(slots: readonly CalendarPlanSlot[], options: ReadOptions): CalendarPost[] {
  return slots.map((slot) => readPost(slot, options));
}

/** Posts by day key, each day in the planner's order. */
export function postsByDay(posts: readonly CalendarPost[]): Map<string, CalendarPost[]> {
  const map = new Map<string, CalendarPost[]>();
  for (const post of posts) {
    const d = new Date(post.slot.scheduledAt);
    const key = dayKey(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
    const list = map.get(key);
    if (list) list.push(post);
    else map.set(key, [post]);
  }
  for (const list of map.values()) list.sort((a, b) => a.slot.order - b.slot.order);
  return map;
}

export function turnCounts(posts: readonly CalendarPost[]): Record<TurnGroup, number> {
  const counts: Record<TurnGroup, number> = { you: 0, client: 0, agent: 0, none: 0 };
  for (const post of posts) counts[post.group] += 1;
  return counts;
}

// ---------------------------------------------------------------- filters

export interface CalendarFilters {
  needsYou: boolean;
  withClient: boolean;
  /** A closed month's filter: the posts that never went live. */
  missed?: boolean;
  channel: CalendarPlatform | null;
  pillarId: string | null;
}

export const NO_FILTERS: CalendarFilters = {
  needsYou: false,
  withClient: false,
  channel: null,
  pillarId: null,
};

export function filtersActive(filters: CalendarFilters): boolean {
  return filters.needsYou || filters.withClient || !!filters.missed || !!filters.channel || !!filters.pillarId;
}

/** Every active filter must hold. Needs you and With the client together match nothing a post can be at once. */
export function matchesFilters(post: CalendarPost, filters: CalendarFilters): boolean {
  if (filters.needsYou && post.group !== "you") return false;
  if (filters.missed && post.posted) return false;
  if (filters.withClient && post.group !== "client") return false;
  if (filters.channel && !post.channels.includes(filters.channel)) return false;
  if (filters.pillarId && post.slot.pillarId !== filters.pillarId) return false;
  return true;
}

export interface PillarCount {
  id: string;
  name: string;
  index: number;
  count: number;
}

export interface FilterCounts {
  needsYou: number;
  withClient: number;
  /** Posts that never went live (a closed month's Missed). */
  missed: number;
  channels: Array<{ platform: CalendarPlatform; count: number }>;
  pillars: PillarCount[];
}

/** The numbers on the filter buttons and in their menus, from the whole month. */
export function filterCounts(
  posts: readonly CalendarPost[],
  pillars: readonly PillarRef[] = [],
): FilterCounts {
  const channelCount = new Map<CalendarPlatform, number>();
  for (const post of posts) {
    for (const platform of post.channels) channelCount.set(platform, (channelCount.get(platform) ?? 0) + 1);
  }
  const pillarCount = new Map<string, PillarCount>();
  const names = new Map(pillars.map((pillar) => [pillar.id, pillar.name]));
  for (const post of posts) {
    const id = post.slot.pillarId;
    if (!id) continue;
    const existing = pillarCount.get(id);
    if (existing) existing.count += 1;
    else
      pillarCount.set(id, {
        id,
        name: post.slot.pillarName || names.get(id) || "Pillar",
        index: post.pillarIndex ?? 0,
        count: 1,
      });
  }
  return {
    needsYou: posts.filter((post) => post.group === "you").length,
    withClient: posts.filter((post) => post.group === "client").length,
    missed: posts.filter((post) => !post.posted).length,
    channels: orderChannels([...channelCount.keys()]).map((platform) => ({
      platform,
      count: channelCount.get(platform) ?? 0,
    })),
    pillars: [...pillarCount.values()].sort((a, b) => a.index - b.index),
  };
}

// ------------------------------------------------------------ status line

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * The header's one sentence, from real data (Figma 08): the total, who needs
 * the team, and who is with the client, with how many of those are late.
 * "5 posts. 3 need you; 2 are with Fatafeat, both late."
 */
export function statusSentence(
  posts: readonly CalendarPost[],
  options: { clientName?: string | null; isGuest: boolean },
): string {
  const total = posts.length;
  if (total === 0) return "No posts yet.";
  const counts = turnCounts(posts);
  const other = options.isGuest ? "the team" : clientLabel(options.clientName);
  const late = posts.filter((post) => post.state.turn.owner === "late").length;
  const parts: string[] = [];
  if (counts.you) parts.push(`${counts.you} ${plural(counts.you, "needs", "need")} you`);
  if (counts.client) {
    const lateWords =
      late === 0
        ? ""
        : late === counts.client
          ? counts.client === 1
            ? ", late"
            : counts.client === 2
              ? ", both late"
              : ", all late"
          : `, ${late} late`;
    parts.push(`${counts.client} ${plural(counts.client, "is", "are")} with ${other}${lateWords}`);
  }
  return parts.length
    ? `${total} ${plural(total, "post", "posts")}. ${parts.join("; ")}.`
    : `${total} ${plural(total, "post", "posts")}.`;
}

/** The sentence while a filter is on: how many of the month's posts it keeps. */
export function filteredSentence(
  matched: number,
  total: number,
  filters: CalendarFilters,
  options: {
    monthName: string;
    clientName?: string | null;
    isGuest: boolean;
    view: "month" | "week" | "list";
    channelName?: string;
    pillarName?: string;
  },
): string {
  const other = options.isGuest ? "the team" : clientLabel(options.clientName);
  const active = [filters.needsYou, filters.withClient, !!filters.missed, !!filters.channel, !!filters.pillarId].filter(Boolean).length;
  // "None of ... is", like "one of ... is": singular for zero and one.
  const verb = (one: string, many: string) => (matched <= 1 ? one : many);
  let phrase = verb("matches the filters", "match the filters");
  if (active === 1) {
    if (filters.needsYou) phrase = verb("needs you", "need you");
    else if (filters.missed) phrase = verb("did not go live", "did not go live");
    else if (filters.withClient) phrase = `${verb("is", "are")} with ${other}`;
    else if (filters.channel) phrase = `${verb("goes", "go")} to ${options.channelName ?? "that channel"}`;
    else if (filters.pillarId) phrase = `${verb("is", "are")} in ${options.pillarName ?? "that pillar"}`;
  }
  const whose = `${options.monthName}'s ${total} ${plural(total, "post", "posts")}`;
  if (matched === 0) return `None of ${whose} ${phrase}.`;
  // Needs you alone reads as the month's own sentence (Figma 08, Month · Needs you on).
  if (active === 1 && filters.needsYou && options.view !== "list") {
    return `${total} ${plural(total, "post", "posts")}. ${matched} ${plural(matched, "needs", "need")} you; the rest are dimmed, not hidden.`;
  }
  const tail = options.view === "list" ? "" : " The rest are dimmed, not hidden.";
  return `${matched} of ${whose} ${phrase}.${tail}`;
}

// ------------------------------------------------------------------ scope

export interface MonthTargets {
  static: number;
  vertical: number;
  horizontal: number;
}

export interface ScopeLine {
  key: string;
  label: string;
  placed: number;
  target: number;
  /** "Static 1 of 1", "Vertical video 3 of 2 · 1 over", "Horizontal video 1 of 2 · 1 short". */
  text: string;
  mismatch: boolean;
}

/** "Closed. 2 posts, both missed; none went live." (Figma 08, Month · a past month). */
export function closedSentence(
  posts: readonly { posted: boolean; slot: { status: string } }[],
): string {
  const total = posts.length;
  if (total === 0) return "Closed. Nothing was planned.";
  const live = posts.filter((post) => post.posted).length;
  const missed = posts.filter((post) => post.slot.status === "MISSED").length;
  const all = (n: number) => (n === 1 ? "" : n === 2 ? "both " : "all ");
  const missedWords = missed === 0 ? "" : missed === total ? `, ${all(total)}missed` : `, ${missed} missed`;
  const liveWords =
    live === 0
      ? total === 1
        ? "it did not go live"
        : "none went live"
      : live === total
        ? total === 1
          ? "it went live"
          : `${all(total)}went live`
        : `${live} went live`;
  return `Closed. ${total} ${total === 1 ? "post" : "posts"}${missedWords}; ${liveWords}.`;
}

/**
 * A planned (or closed) month's scope in one line (Figma 08, Month · December):
 * "5 of 5 posts", "1 static · 3 vertical · 1 horizontal", "2 late". A month's
 * posts are its planned and quick posts. Short of the scope reads in alert ink;
 * late answers too.
 */
export function scopeSummaryLines(
  slots: readonly CalendarPlanSlot[],
  targets: MonthTargets | null,
  late: number | null,
): ScopeLine[] {
  const statics = slots.filter((slot) => slot.mediaType === "STATIC").length;
  const horizontal = slots.filter((slot) => slot.mediaType !== "STATIC" && slot.orientation === "HORIZONTAL").length;
  const vertical = slots.length - statics - horizontal;
  const target = targets ? targets.static + targets.vertical + targets.horizontal : slots.length;
  const short = slots.length < target;
  const lines: ScopeLine[] = [
    {
      key: "total",
      label: "Posts",
      placed: slots.length,
      target,
      text: `${slots.length} of ${target} ${target === 1 ? "post" : "posts"}`,
      mismatch: short,
    },
    {
      key: "mix",
      label: "Mix",
      placed: slots.length,
      target,
      text: `${statics} static · ${vertical} vertical · ${horizontal} horizontal`,
      mismatch: short,
    },
  ];
  // The foot names late posts only when there are some (Figma 08 scope foot).
  if (late !== null && late > 0) {
    lines.push({ key: "late", label: "Late", placed: late, target: 0, text: `${late} late`, mismatch: late > 0 });
  }
  return lines;
}

/**
 * Placed against target per format bucket, off the month's own snapshot. Missed
 * posts did not fill a slot they were sold against, and quick posts sit outside
 * the scope, so neither counts. `countShort: false` while a month is unplanned or
 * planning: only an over is news then.
 */
export function scopeLines(
  slots: readonly CalendarPlanSlot[],
  targets: MonthTargets | null,
  options: { countShort?: boolean } = {},
): ScopeLine[] | null {
  // A month not planned yet, or still being planned, is not short of anything yet.
  const countShort = options.countShort ?? true;
  if (!targets) return null;
  const counted = slots.filter((slot) => slot.status !== "MISSED" && slot.origin !== "DIRECT");
  const statics = counted.filter((slot) => slot.mediaType === "STATIC").length;
  const horizontal = counted.filter(
    (slot) => slot.mediaType !== "STATIC" && slot.orientation === "HORIZONTAL",
  ).length;
  const vertical = counted.length - statics - horizontal;
  const line = (key: ScopeLine["key"], label: string, placed: number, target: number): ScopeLine => {
    const delta = countShort ? placed - target : Math.max(0, placed - target);
    const suffix = delta > 0 ? ` · ${delta} over` : delta < 0 ? ` · ${-delta} short` : "";
    return { key, label, placed, target, text: `${label} ${placed} of ${target}${suffix}`, mismatch: delta !== 0 };
  };
  return [
    line("static", "Static", statics, targets.static),
    line("vertical", "Vertical video", vertical, targets.vertical),
    line("horizontal", "Horizontal video", horizontal, targets.horizontal),
  ];
}

// ------------------------------------------------------------------- drag

export interface DropRule {
  allowed: boolean;
  /** Why a day refuses the post, for the tooltip. */
  reason: string | null;
  /** What the drop does beyond the day, when the server rewrites the status. */
  consequence: string | null;
}

/**
 * Where a lifted post may land. Moves stay inside the open month (the server
 * refuses another month), a closed month refuses every write, and a post the
 * client is deciding on cannot move at all.
 */
export function dropRule(
  slot: Pick<CalendarPlanSlot, "status">,
  target: Pick<DayCell, "year" | "month" | "day" | "inMonth">,
  context: { monthName: string; closed: boolean; clientName?: string | null },
): DropRule {
  if (context.closed) {
    return { allowed: false, reason: `${context.monthName} is closed. Nothing on it moves.`, consequence: null };
  }
  if (slot.status === "IN_CLIENT_REVIEW") {
    return {
      allowed: false,
      reason: `With ${clientLabel(context.clientName)}. It can't move while they decide.`,
      consequence: null,
    };
  }
  if (!target.inMonth) {
    return { allowed: false, reason: `Posts move within ${context.monthName} only.`, consequence: null };
  }
  const next = statusAfterMove(slot.status as CalendarPlanSlot["status"], target);
  const consequence =
    next === slot.status
      ? null
      : next === "MISSED"
        ? "Lands in the past, so it is marked missed."
        : "Back on a future day, so it goes back in the queue for ideas.";
  return { allowed: true, reason: null, consequence };
}

// ------------------------------------------------------------------- list

export interface ListDay {
  key: string;
  day: number;
  weekday: number;
  posts: CalendarPost[];
}

export interface ListMonth {
  year: number;
  month: number;
  label: string;
  days: ListDay[];
}

/** Rows grouped by day, and by month when the list runs across months. */
export function listGroups(posts: readonly CalendarPost[]): ListMonth[] {
  const sorted = [...posts].sort(
    (a, b) =>
      Date.parse(a.slot.scheduledAt) - Date.parse(b.slot.scheduledAt) || a.slot.order - b.slot.order,
  );
  const months: ListMonth[] = [];
  for (const post of sorted) {
    const d = new Date(post.slot.scheduledAt);
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    let group = months.at(-1);
    if (!group || group.year !== year || group.month !== month) {
      group = { year, month, label: `${MONTH_NAMES[month - 1]} ${year}`, days: [] };
      months.push(group);
    }
    const key = dayKey(year, month, d.getUTCDate());
    let day = group.days.at(-1);
    if (!day || day.key !== key) {
      day = { key, day: d.getUTCDate(), weekday: d.getUTCDay(), posts: [] };
      group.days.push(day);
    }
    day.posts.push(post);
  }
  return months;
}

/**
 * Days since the post last changed, from its own `updatedAt`. Null under a day,
 * and null for posts nobody is waiting on.
 */
export function daysSinceChange(post: CalendarPost, now: number): number | null {
  if (post.group !== "you" && post.group !== "client") return null;
  const changed = Date.parse(post.slot.updatedAt);
  if (Number.isNaN(changed)) return null;
  const days = Math.floor((todayUtc(now) - todayUtc(changed)) / DAY_MS);
  return days >= 1 ? days : null;
}

// ------------------------------------------------------------------ phone

export interface ScheduleDay {
  key: string;
  year: number;
  /** 1-12. */
  month: number;
  day: number;
  weekday: number;
  utc: number;
  today: boolean;
  posts: CalendarPost[];
}

export interface ScheduleMonth {
  year: number;
  /** 1-12. */
  month: number;
  /** "2026-12". */
  key: string;
  days: ScheduleDay[];
}

/**
 * Where the phone's schedule starts: today when today is in the open month,
 * else the first of the open month (a later month reads from its start, a past
 * one from its start too).
 */
export function scheduleStart(year: number, month: number, todayUtcValue: number): number {
  const first = Date.UTC(year, month - 1, 1);
  const next = Date.UTC(year, month, 1);
  return todayUtcValue >= first && todayUtcValue < next ? todayUtcValue : first;
}

/** The first of the month before the one `utc` falls in: what "Earlier" reaches back to. */
export function monthBefore(utc: number): number {
  const d = new Date(utc);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1);
}

/**
 * The phone's schedule (DESIGN.md "The Calendar in Parts": PhoneDayAnchor,
 * PhonePostCard, PhoneMonthBanner): one continuous list across months from
 * `fromUtc` forward, each day an anchor with its posts, in time order. When the
 * list starts on today, today keeps its anchor even with nothing on it. The
 * posts before the start are counted, for the Earlier button.
 */
export function phoneSchedule(
  posts: readonly CalendarPost[],
  options: { fromUtc: number; todayUtc: number },
): { months: ScheduleMonth[]; earlier: number } {
  const sorted = [...posts].sort(
    (a, b) =>
      Date.parse(a.slot.scheduledAt) - Date.parse(b.slot.scheduledAt) || a.slot.order - b.slot.order,
  );
  const months: ScheduleMonth[] = [];
  let earlier = 0;
  const dayOf = (utc: number): ScheduleDay => {
    const cell = cellAt(utc, 0, 0);
    return {
      key: cell.key,
      year: cell.year,
      month: cell.month,
      day: cell.day,
      weekday: cell.weekday,
      utc,
      today: utc === options.todayUtc,
      posts: [],
    };
  };
  const place = (day: ScheduleDay) => {
    const key = `${day.year}-${String(day.month).padStart(2, "0")}`;
    let group = months.at(-1);
    if (!group || group.key !== key) {
      group = { year: day.year, month: day.month, key, days: [] };
      months.push(group);
    }
    const last = group.days.at(-1);
    if (last && last.key === day.key) return last;
    group.days.push(day);
    return day;
  };
  if (options.fromUtc === options.todayUtc) place(dayOf(options.todayUtc));
  for (const post of sorted) {
    const utc = todayUtc(Date.parse(post.slot.scheduledAt));
    if (utc < options.fromUtc) {
      earlier += 1;
      continue;
    }
    place(dayOf(utc)).posts.push(post);
  }
  return { months, earlier };
}

export interface CompactDay extends YearDot {
  /** Posts on the day, in the open month. */
  count: number;
  /** At least one of them went live. */
  posted: boolean;
  today: boolean;
  chosen: boolean;
}

/**
 * CompactMonth (the phone's Month): six weeks of seven cells, each a date and a
 * dot in the strongest whose-turn colour, today circled and the chosen day
 * outlined. Neighbouring months' days carry no dot and are never chosen.
 */
export function compactMonthDays(
  year: number,
  month: number,
  posts: readonly CalendarPost[],
  options: { todayUtc: number; chosenKey: string | null },
): CompactDay[] {
  const byDay = postsByDay(posts);
  return yearMonthDots(year, month, posts).map((dot) => {
    const list = dot.cell.inMonth ? (byDay.get(dot.cell.key) ?? []) : [];
    return {
      ...dot,
      count: list.length,
      posted: list.some((post) => post.posted),
      today: dot.cell.utc === options.todayUtc,
      chosen: dot.cell.inMonth && dot.cell.key === options.chosenKey,
    };
  });
}

/** The day the phone's Month opens on: today in this month, else the first day with a post, else the 1st. */
export function defaultChosenDay(
  year: number,
  month: number,
  posts: readonly CalendarPost[],
  todayUtcValue: number,
): number {
  const today = new Date(todayUtcValue);
  if (today.getUTCFullYear() === year && today.getUTCMonth() + 1 === month) return today.getUTCDate();
  const days = posts
    .map((post) => new Date(post.slot.scheduledAt))
    .filter((d) => d.getUTCFullYear() === year && d.getUTCMonth() + 1 === month)
    .map((d) => d.getUTCDate());
  return days.length ? Math.min(...days) : 1;
}

// ------------------------------------------------------------------- year

export interface YearDot {
  cell: DayCell;
  /** The strongest turn on the day: yours first, then the client's, then the rest. */
  group: TurnGroup | null;
}

const GROUP_RANK: Record<TurnGroup, number> = { you: 0, client: 1, agent: 2, none: 3 };

/** A mini month for the year: 42 cells, each a date and at most one dot. */
export function yearMonthDots(year: number, month: number, posts: readonly CalendarPost[]): YearDot[] {
  const byDay = postsByDay(posts);
  return sixWeekGrid(year, month).map((cell) => {
    const list = cell.inMonth ? (byDay.get(cell.key) ?? []) : [];
    const group = list.reduce<TurnGroup | null>(
      (best, post) => (best === null || GROUP_RANK[post.group] < GROUP_RANK[best] ? post.group : best),
      null,
    );
    return { cell, group };
  });
}

/**
 * When a month that is not planned will plan itself: the brand's planning day,
 * `planningPeriodMonths` before the month. `label` is "5 Dec", `ahead` "one
 * month". Null when automatic planning is off, has no day, or the day passed.
 */
export function autoPlanRun(
  settings:
    | { autoPlanEnabled: boolean; planningPeriodMonths: number; planDayOfMonth?: number | null }
    | null
    | undefined,
  year: number,
  month: number,
  now: number,
): { utc: number; label: string; ahead: string } | null {
  if (!settings?.autoPlanEnabled || !settings.planDayOfMonth) return null;
  const ahead = Math.max(1, settings.planningPeriodMonths);
  const index = year * 12 + (month - 1) - ahead;
  const runMonth = index % 12;
  const utc = Date.UTC(Math.floor(index / 12), runMonth, settings.planDayOfMonth);
  if (utc < todayUtc(now)) return null;
  return {
    utc,
    label: `${settings.planDayOfMonth} ${MONTH_NAMES[runMonth].slice(0, 3)}`,
    ahead: ["one month", "two months", "three months"][ahead - 1] ?? `${ahead} months`,
  };
}
